import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search, Phone, PhoneCallback, PhoneMissed } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useGetTwilioConfigurationsQuery, useGetTwilioCallsQuery } from '../store/api/twilioApi';

export const TwilioCallsPage: React.FC = () => {
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const { token, isAuthenticated } = useSelector((state: any) => state.auth);
  const pageSize = 25;

  // Get configurations
  const { data: configurations, isLoading: configsLoading } = useGetTwilioConfigurationsQuery();
  
  // Get calls data
  const { 
    data: calls, 
    isLoading: callsLoading, 
    error: callsError 
  } = useGetTwilioCallsQuery(
    { 
      configId: selectedConfigId!, 
      limit: pageSize, 
      offset: (page - 1) * pageSize 
    },
    { skip: !selectedConfigId }
  );

  // Set first configuration as default
  useEffect(() => {
    if (configurations && configurations.length > 0 && !selectedConfigId) {
      setSelectedConfigId(configurations[0].id);
    }
  }, [configurations, selectedConfigId]);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <Phone color="success" />;
      case 'busy':
      case 'no-answer':
        return <PhoneMissed color="warning" />;
      case 'failed':
        return <PhoneCallback color="error" />;
      default:
        return <Phone />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'busy':
      case 'no-answer':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (duration: number) => {
    if (!duration) return '0s';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredCalls = calls?.filter(call => {
    const matchesSearch = !searchTerm || 
      call.from_number?.includes(searchTerm) || 
      call.to_number?.includes(searchTerm) ||
      call.sid?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || call.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) || [];

  // Calculate totals for different statuses
  const statusTotals = React.useMemo(() => {
    if (!calls) {
      return {
        all: 0,
        completed: 0,
        busy: 0,
        'no-answer': 0,
        failed: 0
      };
    }
    
    const totals = {
      all: calls.length,
      completed: 0,
      busy: 0,
      'no-answer': 0,
      failed: 0
    };
    
    calls.forEach(call => {
      const status = call.status?.toLowerCase();
      if (status === 'completed') totals.completed++;
      else if (status === 'busy') totals.busy++;
      else if (status === 'no-answer') totals['no-answer']++;
      else if (status === 'failed') totals.failed++;
    });
    
    return totals;
  }, [calls]);

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">Please log in to view Twilio calls.</Alert>
      </Box>
    );
  }

  if (configsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!configurations || configurations.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Twilio Calls
        </Typography>
        <Alert severity="info">
          No Twilio configurations found. Please create a configuration first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Twilio Calls
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        View and manage your Twilio call logs
      </Typography>

      {/* Configuration Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Configuration</InputLabel>
                <Select
                  value={selectedConfigId || ''}
                  onChange={(e) => setSelectedConfigId(Number(e.target.value))}
                  label="Configuration"
                >
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                placeholder="Search calls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="busy">Busy</MenuItem>
                  <MenuItem value="no-answer">No Answer</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Status Totals */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Call Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center" p={2} bgcolor="primary.light" borderRadius={1}>
                <Typography variant="h4" color="primary.contrastText">
                  {statusTotals.all || 0}
                </Typography>
                <Typography variant="body2" color="primary.contrastText">
                  Total Calls
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={1}>
                <Typography variant="h4" color="success.contrastText">
                  {statusTotals.completed || 0}
                </Typography>
                <Typography variant="body2" color="success.contrastText">
                  Completed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={1}>
                <Typography variant="h4" color="warning.contrastText">
                  {statusTotals.busy || 0}
                </Typography>
                <Typography variant="body2" color="warning.contrastText">
                  Busy
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center" p={2} bgcolor="info.light" borderRadius={1}>
                <Typography variant="h4" color="info.contrastText">
                  {statusTotals['no-answer'] || 0}
                </Typography>
                <Typography variant="body2" color="info.contrastText">
                  No Answer
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center" p={2} bgcolor="error.light" borderRadius={1}>
                <Typography variant="h4" color="error.contrastText">
                  {statusTotals.failed || 0}
                </Typography>
                <Typography variant="body2" color="error.contrastText">
                  Failed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box textAlign="center" p={2} bgcolor="grey.200" borderRadius={1}>
                <Typography variant="h4" color="text.primary">
                  {filteredCalls.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Filtered Results
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Calls Table */}
      <Card>
        <CardContent>
          {callsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : callsError ? (
            <Alert severity="error">
              Error loading calls: {callsError.toString()}
            </Alert>
          ) : filteredCalls.length === 0 ? (
            <Alert severity="info">
              No calls found for the selected configuration.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCalls.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(call.status || '')}
                            <Chip
                              label={call.status || 'Unknown'}
                              color={getStatusColor(call.status || '') as any}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{call.from_number || 'N/A'}</TableCell>
                        <TableCell>{call.to_number || 'N/A'}</TableCell>
                        <TableCell>{formatDuration(call.duration || 0)}</TableCell>
                        <TableCell>
                          <Chip
                            label={call.direction || 'Unknown'}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{call.start_time ? formatDate(call.start_time) : 'N/A'}</TableCell>
                        <TableCell>{call.end_time ? formatDate(call.end_time) : 'N/A'}</TableCell>
                        <TableCell>
                          {call.price ? `${call.price} ${call.price_unit || ''}` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={Math.ceil((calls?.length || 0) / pageSize)}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
